import { useFrame, useThree } from "@react-three/fiber";
import { useRef } from "react";
import {
  AmbientLight,
  type Camera,
  DirectionalLight,
  HemisphereLight,
  type Object3D,
  SpotLight,
  Vector3,
} from "three";

export const PRODUCT_VIEWER_LIGHTING = {
  ambient: {
    intensity: 7,
  },
  primarySpot: {
    position: [10, 10, 10] as const,
    angle: 0.15,
    penumbra: 1,
    intensity: 2,
  },
  secondarySpot: {
    position: [-10, -10, -10] as const,
    intensity: 1.2,
  },
  hemisphere: {
    intensity: 1,
    groundColor: "white",
  },
  cameraFill: {
    intensity: 2,
    targetDistance: 100,
  },
} as const;

const cameraForward = new Vector3();
const cameraTarget = new Vector3();

export function syncProductViewerCameraLight(
  light: DirectionalLight | null | undefined,
  camera: Camera,
) {
  if (!light) return;
  light.position.copy(camera.position);
  camera.getWorldDirection(cameraForward);
  cameraTarget
    .copy(camera.position)
    .addScaledVector(
      cameraForward,
      PRODUCT_VIEWER_LIGHTING.cameraFill.targetDistance,
    );
  light.target.position.copy(cameraTarget);
  light.target.updateMatrixWorld();
}

// Fill light from the camera direction so surfaces facing the viewer are lit and
// surface structure (normals) is visible without rotating the model.
export const CameraLight = ({
  intensity = PRODUCT_VIEWER_LIGHTING.cameraFill.intensity,
}) => {
  const lightRef = useRef<DirectionalLight>(null);
  const { camera } = useThree();

  useFrame(() => {
    syncProductViewerCameraLight(lightRef.current, camera);
  });

  return (
    <directionalLight ref={lightRef} intensity={intensity} castShadow={false} />
  );
};

export const ProductViewerLighting = ({
  includeCameraLight = true,
}: {
  includeCameraLight?: boolean;
}) => (
  <>
    <ambientLight intensity={PRODUCT_VIEWER_LIGHTING.ambient.intensity} />
    <spotLight
      position={[...PRODUCT_VIEWER_LIGHTING.primarySpot.position]}
      angle={PRODUCT_VIEWER_LIGHTING.primarySpot.angle}
      penumbra={PRODUCT_VIEWER_LIGHTING.primarySpot.penumbra}
      intensity={PRODUCT_VIEWER_LIGHTING.primarySpot.intensity}
    />
    <spotLight
      position={[...PRODUCT_VIEWER_LIGHTING.secondarySpot.position]}
      intensity={PRODUCT_VIEWER_LIGHTING.secondarySpot.intensity}
    />
    <hemisphereLight
      intensity={PRODUCT_VIEWER_LIGHTING.hemisphere.intensity}
      groundColor={PRODUCT_VIEWER_LIGHTING.hemisphere.groundColor}
    />
    {includeCameraLight ? <CameraLight /> : null}
  </>
);

export function createProductViewerLights({
  includeCameraLight = true,
}: {
  includeCameraLight?: boolean;
} = {}) {
  const ambient = new AmbientLight(
    0xffffff,
    PRODUCT_VIEWER_LIGHTING.ambient.intensity,
  );
  const primarySpot = new SpotLight(
    0xffffff,
    PRODUCT_VIEWER_LIGHTING.primarySpot.intensity,
  );
  primarySpot.position.set(...PRODUCT_VIEWER_LIGHTING.primarySpot.position);
  primarySpot.angle = PRODUCT_VIEWER_LIGHTING.primarySpot.angle;
  primarySpot.penumbra = PRODUCT_VIEWER_LIGHTING.primarySpot.penumbra;

  const secondarySpot = new SpotLight(
    0xffffff,
    PRODUCT_VIEWER_LIGHTING.secondarySpot.intensity,
  );
  secondarySpot.position.set(...PRODUCT_VIEWER_LIGHTING.secondarySpot.position);

  const hemisphere = new HemisphereLight(
    0xffffff,
    0xffffff,
    PRODUCT_VIEWER_LIGHTING.hemisphere.intensity,
  );

  const lights: Object3D[] = [ambient, primarySpot, secondarySpot, hemisphere];
  if (!includeCameraLight) {
    return { lights, cameraLight: null };
  }

  const cameraLight = new DirectionalLight(
    0xffffff,
    PRODUCT_VIEWER_LIGHTING.cameraFill.intensity,
  );
  cameraLight.castShadow = false;
  lights.push(cameraLight, cameraLight.target);
  return { lights, cameraLight };
}
